import { useState, useEffect } from 'react';
import {
  useTranslation,
  Toggle,
  ToggleStyle,
  Theme,
  ButtonSize,
  Button,
  ButtonStyles,
  TextLink,
  TextLinkStyle,
  AccountType,
  NextgenImage,
  ButtonWidth,
  useBreakpoint,
  useSession,
  LoaderType,
} from '@nextgen-web-framework/all';
import {
  updateCustomerType,
  updateRegistrationData,
  type DynamicObjectInterface,
  type ImageReturnType,
} from '@commerce-profile-webcomponents/sdk';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import RegistrationSignIn from '../RegistrationSignIn/RegistrationSignIn';
import HtmlContent from '../Common/HtmlContent/HtmlContent';
import { useLocale } from '../../utils/customHooks';
import SkeletonLoader from '../Common/skeleton-loader/SkeletonLoader';
import styles from './RegistrationSelectAccountType.module.scss';
import { RegistrationSelectUserTypeProps, AccountTypeValuesType } from './types/propTypes';

const RegistrationSelectAccountType = ({
  nextHandler,
  isEditable = false,
  isRegistrationBanner = false,
}: RegistrationSelectUserTypeProps) => {
  const locale = useLocale();
  const { Customer, BusinessOwner, Member } = AccountType;
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { selectAccountType, findOutMoreUrl, currentIndex, sourceUrl } = registrationFlowData;
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const termsNConditionsCmsData = useSelector((state: AppState) => state.commerceProfile.termsNConditionsCMS);

  const viewPortType = useBreakpoint();
  const [image, setImage] = useState<ImageReturnType>();
  const [landingPageDetails, setLandingPageDetails] = useState<string>();

  const accountTypeValues: AccountTypeValuesType = {
    MEMBER: {
      selectAccountType: Member,
    },
    BUSINESS_OWNER: {
      selectAccountType: BusinessOwner,
    },
    CUSTOMER: {
      selectAccountType: Customer,
    },
  };
  const { session } = useSession();
  const { aboNum: accountNum = '' } = session;

  const accountTypes = selectAccountType?.accountTypes;
  const [tabIndex, setTabIndex] = useState(1);
  const t = useTranslation();
  const tabData = accountTypes?.map((account: string) => t(`profile.common.selectAccountType.${account}`)) ?? [];

  const dispatch = useDispatch<AppDispatch>();
  const onToggel = (e: DynamicObjectInterface) => {
    const { selectAccountType } = accountTypeValues[accountTypes[e.index - 1]];
    dispatch(updateCustomerType(selectAccountType));
    setTabIndex(e.index);
  };

  const registerHandler = () => {
    nextHandler?.();
  };

  useEffect(() => {
    dispatch(
      updateRegistrationData({
        userData: {
          registrationId: '',
        },
      }),
    );

    if (selectedAccountType) {
      setTabIndex(accountTypes.indexOf(selectedAccountType) + 1);
    }
  }, []);

  useEffect(() => {
    setLandingPageDetails(undefined);
  }, [locale, selectedAccountType]);

  useEffect(() => {
    if (accountTypeImagesCmsData && selectedAccountType && accountTypeImagesCmsData[selectedAccountType]) {
      const { landingPageImage } = accountTypeImagesCmsData[selectedAccountType];
      setImage(landingPageImage?.find((item: ImageReturnType) => item?.viewport.includes(viewPortType)));
    }
  }, [selectedAccountType, accountTypeImagesCmsData, viewPortType]);

  useEffect(() => {
    if (termsNConditionsCmsData?.[selectedAccountType]) {
      const { landingPageDetails } = termsNConditionsCmsData[selectedAccountType];
      setLandingPageDetails(landingPageDetails);
    }
  }, [selectedAccountType, termsNConditionsCmsData, locale]);

  return (
    <div
      className={`${styles['select-user-type-container']} ${isRegistrationBanner ? styles['padding'] : ''}`}
      data-testid="select-user-type-container"
    >
      {!isRegistrationBanner && (
        <div className={styles['profile-header-logo']} data-testid="profile-header-image">
          <NextgenImage
            src={`/images/AmwayDesktopLogo.png`}
            isRelativeURL={true}
            loaderType={LoaderType.WEBFONTS}
            alt="headerLogo"
            objectFit="contain"
          />
        </div>
      )}

      {!isRegistrationBanner && (
        <div className={styles['select-user-type-heading']} data-testid="select-user-type-heading">
          {t('profile.registrationSelectAccountType.heading')}
        </div>
      )}
      {(!isRegistrationBanner || isEditable) && (
        <div
          className={`${styles['select-user-type-toggle']} ${isRegistrationBanner ? styles['padding'] : ''}`}
          data-testid="select-user-type-toggle"
        >
          <Toggle
            toggleStyle={ToggleStyle.TABS}
            tabList={tabData}
            theme={Theme.PURPLE}
            activeTab={tabIndex}
            onClick={onToggel}
          />
        </div>
      )}
      <div
        className={`${!isRegistrationBanner ? styles['select-user-type-details'] : ''}`}
        data-testid="select-user-type-details"
      >
        {image ? (
          <div className={styles['select-user-type-image']}>
            <NextgenImage src={image.url} loaderType={LoaderType.CONTENTSTACK} alt={image.altText} fill={true} />
          </div>
        ) : (
          <SkeletonLoader />
        )}
        <div className={styles['select-user-type-sub-heading']}>
          <span className={styles['select-user-type-as']}>{t('profile.registrationSelectAccountType.details')}</span>
          <span className={styles['select-user-type-abo-customer']} data-testid="select-user-type-abo-customer">
            {t(`profile.common.selectAccountType.${selectedAccountType}`)}
          </span>
        </div>
        {landingPageDetails ? (
          <div className={styles['select-user-type-list']} data-testid="select-user-type-list">
            <HtmlContent content={landingPageDetails} />
          </div>
        ) : (
          <SkeletonLoader />
        )}
        {!isRegistrationBanner && (
          <div className={styles['select-user-type-find-out-more']}>
            <TextLink
              id="select-user-type-find-out-more"
              data-testid="select-user-type-find-out-more"
              text={t('profile.registrationSelectAccountType.FindOutMore')}
              textLinkStyle={TextLinkStyle.UNDERLINE}
              theme={Theme.PURPLE}
              href={findOutMoreUrl}
            />
          </div>
        )}
        {!isRegistrationBanner && (
          <div className={styles['select-user-type-register-button']}>
            <Button
              data-testId="register-button-id"
              title={t(`profile.registrationSelectAccountType.button.${selectedAccountType}`)}
              buttonStyle={ButtonStyles.SOLID}
              size={ButtonSize.MEDIUM}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              onClick={registerHandler}
            />
          </div>
        )}
      </div>

      {currentIndex === 0 && !isRegistrationBanner && !accountNum && <RegistrationSignIn sourceUrl={sourceUrl} />}
    </div>
  );
};

export default RegistrationSelectAccountType;
